//Libs
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

// Components
import {
  Flexbox,
  InformationContainer,
  SingleSelect,
  Heading4
} from '@ibinder/common-frontend';
import ListDocument from './ListDocument';
import AdminActionBar from './AdminActionBar';
import AdminModal from './AdminModal';
import CreateOrgForm from './CreateOrgForm';
import UploadTemplateForm from '../../../../form/UploadTemplateForm';
import { PrintProviders } from '../../../../PrintProviders/pages/PrintProvidersList';

// Queries
import {
  GET_ALL_ORGS,
  GET_ALL_USERS,
  GET_ALL_CUSTOMERS,
  GET_ALL_MARKETS,
  BINDER_SEARCH,
  TENDER_SEARCH,
  DELETED_BINDER_SEARCH
} from '../../../../../api/queries/queries_local';

// Utils
import useCheckDocument from 'Shared/Utils/useCheckDocument';
import { useToggleWithClickOutside } from 'Shared/hooks/useToggleWithClickOutside';

// Temp
import {
  orgColumns,
  userColumns,
  customerColumns,
  tenderColumns,
  binderColumns
} from './ListDocument/dummyData';
import AdminForm from '../../../../../components/form/AdminForm';
import CreateFacilityStructure from '../../../../form/CreateFacilityStructure';
import CreateIssueTemplate from '../../../../IssueTemplates/CreateIssueTemplate';
import GlobalBinderTemplates from '../../../../GlobalTemplates/GlobalBinderTemplates';
import InspectionTypeTemplates from '../../../../InspectionTemplates/InspectionTypeTemplates';
import { encodeUserSearchQuery } from '@ibinder/common-utils';
import { BatchOperations } from '../../../../BatchOperations/Utils/OperationsRenderer';
import { iBinderHeaderEnum } from '@ibinder/ui-common-models';
import { ListWorkFlows } from '../../../../GlobalWorkflowTemplates/ListWorkFlows';
import UserResourceAccess from './Users/UserResourceAccess';

const LayoutBinderInformationFlex = styled(Flexbox)`
  @media (max-width: ${(p) => p.theme.devices.sm}px) {
    flex-direction: column;
  }
`;
const NumberOfDocumentsInfo = styled(InformationContainer)`
  margin-left: 0;
  @media (max-width: ${(p) => p.theme.devices.xs}px) {
    margin-left: 0.5rem;
  }
`;
const Container = styled.article`
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`;
const BinderInformationFlex = styled(Flexbox)`
  > div {
    margin-left: 0;
    margin-right: 1rem;
    @media (max-width: ${(p) => p.theme.devices.xs}px) {
      margin-left: 0.5rem;
    }
  }
  section {
    min-width: 230px;
    > div {
      margin-left: 0;
      margin-right: 0;
    }
  }
  @media (max-width: ${(p) => p.theme.devices.sm}px) {
    flex-wrap: wrap;
  }
`;

const types = {
  users: 'Users',
  organizations: 'Organizations',
  customers: 'Customers',
  document: 'Document',
  binders: 'Binders',
  templates: 'BinderTemplateCreate',
  globalBinderTemplates: 'GlobalBinderTemplateList',
  facilites: 'FacilityStructureCreate',
  issueTemplates: 'IssueTemplateCreate',
  inspectionTypeTemplate: 'InspectionTypeTemplateForm',
  inspectionGlobalTemplates: 'InspectionGlobalTemplates',
  applicationTabTemplates: 'ApplicationTabTemplates',
  tenders: 'Tenders',
  printProviders: 'PrintProviders',
  batchOperations: 'BatchOperations',
  deletedBinders: 'DeletedBinders'
} as const;

const ListContainer = ({ type }) => {
  const { t } = useTranslation();

  const querySettings = (dataCb, objCb) => {
    const settings = {
      onCompleted: () => {
        setLocalData(dataCb());
        setCurrentDataObj(objCb());
      }
    };
    return settings;
  };

  const [getBinderSearch, binders] = useLazyQuery(BINDER_SEARCH, {
    fetchPolicy: 'no-cache',
    ...querySettings(
      () => binders.data.get.data,
      () => binders
    ),
    onCompleted: () => {
      if (!data) {
        setLocalData(binders.data.get.data);
      } else {
        setLocalData([...data, ...binders.data.get.data]);
      }
      setCurrentDataObj(binders);
    },
    context: {
      headers: { [iBinderHeaderEnum.TENANTID]: '' } // Needs to be empty string to override automatic set tenantId
    }
  });

  const [getTenderSearch, tenders] = useLazyQuery(TENDER_SEARCH, {
    fetchPolicy: 'no-cache',
    context: {
      headers: { [iBinderHeaderEnum.TENANTID]: '' } // Needs to be empty string to override automatic set tenantId
    },
    ...querySettings(
      () => tenders.data.get.data,
      () => tenders
    ),
    onCompleted: () => {
      if (!data) {
        setLocalData(tenders.data.get.data);
      } else {
        setLocalData([...data, ...tenders.data.get.data]);
      }
      setCurrentDataObj(tenders);
    }
  });

  const [getOrgs, orgs] = useLazyQuery(GET_ALL_ORGS, {
    fetchPolicy: 'no-cache',
    ...querySettings(
      () => orgs.data.get,
      () => orgs
    ),
    onCompleted: () => {
      setLocalData(orgs.data.get);
      setCurrentDataObj(orgs);
      setTotalCount(orgs.data.get.length);
    }
  });

  const [getCustomers, customers] = useLazyQuery(GET_ALL_CUSTOMERS, {
    fetchPolicy: 'no-cache',
    ...querySettings(
      () => customers.data.get,
      () => customers
    ),
    onCompleted: () => {
      if (marketsData?.get) {
        const enrichedCustomers = customers.data.get.map((customer: any) => {
          if (customer.marketId && !customer.market) {
            const market = marketsData.get.find((m: any) => m.id === customer.marketId);
            return { ...customer, market };
          }
          return customer;
        });
        setLocalData(enrichedCustomers);
      } else {
        setLocalData(customers.data.get);
      }
      setCurrentDataObj(customers);
      setTotalCount(customers.data.get.length);
    }
  });

  const [getMarkets, markets] = useLazyQuery(GET_ALL_MARKETS, {
    fetchPolicy: 'no-cache'
  });

  const marketsData = markets.data;

  const [getUsers, users] = useLazyQuery(GET_ALL_USERS, {
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      if (!data) {
        setLocalData(users.data.get.data);
      } else {
        setLocalData([...data, ...users.data.get.data]);
      }
      setCurrentDataObj(users);
    }
  });

  const [data, setLocalData] = useState([]);
  const [currentDataObj, setCurrentDataObj] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [sortingFilter, setSorting] = useState(undefined);
  const [activeIndex, setActiveIndex] = useState(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const [getDeletedBinderSearch, deletedBinders] = useLazyQuery(
    DELETED_BINDER_SEARCH,
    {
      fetchPolicy: 'no-cache',
      ...querySettings(
        () => deletedBinders.data.get.data,
        () => deletedBinders
      ),
      onCompleted: () => {
        if (!data) {
          setLocalData(deletedBinders.data.get.data);
        } else {
          setLocalData([...data, ...deletedBinders.data.get.data]);
        }
        setCurrentDataObj(deletedBinders);
      },
      context: {
        headers: { [iBinderHeaderEnum.TENANTID]: '' } // Needs to be empty string to override automatic set tenantId
      }
    }
  );

  const [
    selectedIds,
    setSelectedIds,
    selectItem,
    selectItems,
    groupCheckStatus
  ] = useCheckDocument();

  const changeSorting = () => {
    if (sortingFilter === 'NOSORT') {
      setSorting('ASC');
    } else if (sortingFilter === 'ASC') {
      setSorting('DESC');
    } else if (sortingFilter === 'DESC') {
      setSorting('NOSORT');
    }
  };
  const changeActiveItem = (index) => {
    setActiveIndex(index);
    setSorting('ASC');
  };
  const onSort = (sortBy) => {
    const index = columns.findIndex((e) => e.accessor === sortBy.id);

    if (sortBy.desc === null) {
      setSorting('NOSORT');
      setActiveIndex(undefined);
    } else {
      index > -1 ? setActiveIndex(index) : setActiveIndex(undefined);
      setSorting(sortBy.desc ? 'DESC' : 'ASC');
    }
  };

  const getFilteredData = (arr) => {
    let data = arr.filter((item) => {
      const [nameKey] = Object.keys(item).filter((j) =>
        j.toLowerCase().includes('name')
      );
      const [idKey] = Object.keys(item).filter((j) =>
        j.toLowerCase().includes('id')
      );
      return (
        item[nameKey].toLowerCase().includes(searchQuery.toLowerCase()) ||
        item[idKey].toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
    return data;
  };

  // On search
  const updateData = (isRefetch) => {
    let sortProp = columns[activeIndex]?.Header ?? '';
    let sortingOrder = sortingFilter ?? '';
    let page;
    if (isRefetch) {
      page = pageNumber + 1;
    } else {
      page = 1;
      setLocalData([]);
    }

    setPageNumber(page);
    switch (type) {
      case types.binders:
        getBinderSearch({
          variables: {
            searchTerm: encodeURIComponent(searchQuery),
            pageNumber: page,
            pageSize: pageSize
          }
        });
        break;
      case types.tenders:
        getTenderSearch({
          variables: {
            searchTerm: encodeURIComponent(searchQuery),
            pageNumber: page,
            pageSize: pageSize,
            sortOrder: sortingOrder,
            sortProperty: sortProp
          }
        });
        break;
      case types.organizations:
        if (!orgs.data || isRefetch) {
          getOrgs();
        } else orgs.data && setLocalData(getFilteredData(orgs.data.get));
        return false;
      case types.customers:
        if (!customers.data || isRefetch) {
          if (!marketsData) {
            getMarkets().then(() => {
              getCustomers();
            });
          } else {
            getCustomers();
          }
        } else customers.data && setLocalData(getFilteredData(customers.data.get));
        return false;
      case types.users:
        if (isRefetch && data.length > 0) fetchMoreUsers();
        else {
          // On search
          setLocalData([]);
          getUsers({
            variables: {
              query: `query=${encodeUserSearchQuery(
                searchQuery
              )}&sortOrder=${sortingOrder}&sortProperty=${sortProp}`
            }
          });
        }
        return users.data?.get.nextPage !== null;
      case types.deletedBinders: {
        getDeletedBinderSearch({
          variables: {
            searchTerm: encodeURIComponent(searchQuery),
            pageNumber: page,
            pageSize: pageSize
          }
        });
        return page * pageSize < deletedBinders.data?.get.totalCount;
      }
      default:
        return null;
    }

    return page * pageSize < binders.data?.get.totalCount;
  };

  const hasFetchMore = () => {
    if (currentDataObj?.data.get?.nextPage) return true;

    if (pageNumber * pageSize < currentDataObj?.data?.get.totalCount)
      return true;
    return false;
  };

  const fetchMoreUsers = () => {
    if (users.data?.get.nextPage) {
      const url = users.data.get.nextPage;
      const params = url.substring(url.indexOf('?') + 1);
      getUsers({
        variables: {
          query: `query=${encodeUserSearchQuery(searchQuery)}&${params}`
        }
      });
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      updateData(false);
    }, 300);

    return () => {
      clearTimeout(delay);
    };
  }, [searchQuery, sortingFilter, activeIndex, type]);

  useEffect(() => {
    setSelectedUser(null);
    return () => {
      setSearchQuery('');
    };
  }, [type]);

  const columns =
    {
      [types.organizations]: orgColumns,
      [types.customers]: customerColumns,
      [types.deletedBinders]: binderColumns.filter(
        (col) => col.id !== 'binderState'
      ),
      [types.binders]: binderColumns,
      [types.users]: userColumns,
      [types.tenders]: tenderColumns
    }[type] ?? [];

  if (type === types.document) {
    return <AdminForm />;
  }

  if (type === types.templates) {
    return <UploadTemplateForm />;
  }

  if (type === types.facilites) {
    return <CreateFacilityStructure />;
  }

  if (type === types.issueTemplates) {
    return <CreateIssueTemplate />;
  }

  if (type === types.globalBinderTemplates) {
    return <GlobalBinderTemplates />;
  }

  if (type === types.inspectionTypeTemplate) {
    return <InspectionTypeTemplates />;
  }
  if (type === types.inspectionGlobalTemplates) {
    return <ListWorkFlows key={type} />;
  }
  if (type === types.applicationTabTemplates) {
    return <ListWorkFlows key={type} tabTypesOnly />;
  }
  if (type === types.printProviders) {
    return <PrintProviders />;
  }
  if (type === types.batchOperations) {
    return <BatchOperations />;
  }
  if (type === types.users && selectedUser) {
    return (
      <UserResourceAccess
        userId={selectedUser.id}
        userName={selectedUser.fullName || selectedUser.email}
        onBack={() => setSelectedUser(null)}
      />
    );
  }
  const loading =
    orgs.loading || customers.loading || users.loading || binders.loading || tenders.loading;

  return (
    <Container>
      <LayoutBinderInformationFlex justifyContent="space-between">
        <NumberOfDocumentsInfo>
          {loading ? (
            <Heading4 loading={loading} label="" />
          ) : (
            <span>
              {`Showing `}
              <strong>
                {type === types.organizations || type === types.customers
                  ? totalCount
                  : `${currentDataObj?.data?.get?.totalCount ?? 0}`}
              </strong>
              {` ${type}`}
            </span>
          )}
        </NumberOfDocumentsInfo>
        {/* THIS IS THE SORTING BAR */}
        <BinderInformationFlex>
          <SingleSelect
            loading={false}
            label={'sortBy'}
            withSortButton={true}
            activeIndex={activeIndex}
            dropdownData={columns
              .filter((item) => !item.disableSortBy)
              .map((item, i) => ({
                Key: i,
                Value: item.Header
              }))}
            dropdownText="Sort by.."
            onClickSorting={changeSorting}
            onClickSelectItem={changeActiveItem}
            sorting={sortingFilter}
          />
        </BinderInformationFlex>
      </LayoutBinderInformationFlex>
      {/* THIS IS THE ACTION BAR */}
      <AdminActionBar
        loading={false}
        selectedFilesDocumentId={selectedIds}
        setSelectedIds={setSelectedIds}
        {...{ searchQuery, setSearchQuery, type, data }}
        refreshOrgList={() => getOrgs()}
        refreshCustomerList={() => getCustomers()}
        refetchList={() => updateData(true)}
        onViewUserAccess={setSelectedUser}
      />
      {/* THIS IS THE TABLE */}
      <ListDocument
        {...{
          data,
          columns,
          onSort,
          sortingFilter,
          selectedIds,
          setSelectedIds,
          fetchMore: updateData,
          pageCount: pageSize,
          totalCount,
          // pageCount: users.data?.get.pageCount,
          // totalCount: users.data?.get.totalCount,
          hasFetchMore,
          loading,
          type
        }}
        sortBy={{
          desc: sortingFilter === 'NOSORT' ? null : sortingFilter === 'DESC',
          id:
            activeIndex !== undefined
              ? columns[activeIndex].accessor
              : undefined
        }}
      />
    </Container>
  );
};

export default ListContainer;
